const STORAGE_KEY = 'brix.inventoryTransactionsTable';

export type SortDir = 'asc' | 'desc';

export type InventorySortField =
  | 'movement_type'
  | 'item_name'
  | 'txn_date'
  | 'qty_change'
  | 'unit_cost'
  | 'value_change'
  | 'qty_on_hand_after'
  | 'reference_number'
  | 'counterparty_name';

export const INVENTORY_SORT_FIELDS: readonly InventorySortField[] = [
  'movement_type',
  'item_name',
  'txn_date',
  'qty_change',
  'unit_cost',
  'value_change',
  'qty_on_hand_after',
  'reference_number',
  'counterparty_name',
] as const;

const INVENTORY_SORT_FIELD_SET = new Set<string>(INVENTORY_SORT_FIELDS);

export type InventoryTablePreferences = {
  sort: InventorySortField | `-${InventorySortField}`;
  perPage: number;
};

const DEFAULT_PREFERENCES: InventoryTablePreferences = {
  sort: '-txn_date',
  perPage: 25,
};

export const encodeInventorySort = (
  field: InventorySortField,
  direction: SortDir
): InventoryTablePreferences['sort'] => (direction === 'desc' ? `-${field}` : field);

export const parseInventorySort = (
  value: string
): { field: InventorySortField; direction: SortDir } | null => {
  const direction: SortDir = value.startsWith('-') ? 'desc' : 'asc';
  const field = (value.startsWith('-') ? value.slice(1) : value) as InventorySortField;

  if (!INVENTORY_SORT_FIELD_SET.has(field)) {
    return null;
  }

  return { field, direction };
};

export const loadInventoryTablePreferences = (): InventoryTablePreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<InventoryTablePreferences>;
    const sort =
      typeof parsed.sort === 'string' && parseInventorySort(parsed.sort)
        ? (parsed.sort as InventoryTablePreferences['sort'])
        : DEFAULT_PREFERENCES.sort;
    const perPage =
      typeof parsed.perPage === 'number' && parsed.perPage >= 1 && parsed.perPage <= 100
        ? parsed.perPage
        : DEFAULT_PREFERENCES.perPage;

    return { sort, perPage };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const saveInventoryTablePreferences = (prefs: InventoryTablePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / private browsing errors.
  }
};
